import { useCallback, useRef, useEffect } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const RECAPTCHA_POLL_INTERVAL_MS = 200;
const RECAPTCHA_POLL_TIMEOUT_MS = 5000;

export const useGoogleRecaptchaV3 = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const executeRecaptchaRef = useRef(executeRecaptcha);

  useEffect(() => {
    executeRecaptchaRef.current = executeRecaptcha;
  }, [executeRecaptcha]);

  const setRecaptchaToken = useCallback(async (action: string) => {
    if (!process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_V3_SITE_KEY) return;

    // Wait for the global reCAPTCHA script to finish loading during the
    // initial app bootstrap before trying to execute an action.
    let execute = executeRecaptchaRef.current;
    execute ??= await new Promise<typeof executeRecaptcha>(resolve => {
      const start = Date.now();
      const interval = setInterval(() => {
        if (executeRecaptchaRef.current) {
          clearInterval(interval);
          resolve(executeRecaptchaRef.current);
        } else if (Date.now() - start >= RECAPTCHA_POLL_TIMEOUT_MS) {
          clearInterval(interval);
          resolve(undefined);
        }
      }, RECAPTCHA_POLL_INTERVAL_MS);
    });

    if (!execute) return;

    try {
      const recaptchaToken = await execute(action);
      sessionStorage.setItem('recaptchaV3Token', recaptchaToken);
      return recaptchaToken;
    } catch {
      // GoogleReCaptchaProvider is unavailable or the script is not ready yet.
      // Silently skip reCAPTCHA to preserve the current caller behavior.
      return;
    }
  }, []);

  return { setRecaptchaToken };
};
